import { useCallback, useContext, useEffect, useState } from "react";

import { useSelector } from "react-redux";

import {
  HostDisintermediationRiskLevel,
  isHostDisintermediationRiskLevel,
} from "@ps-design-system/components/HostDisintermediation";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { StatsigContext } from "../../elements-statsig-provider/statsig-context";
import {
  useGetHostPlatformRiskQuery,
  useAcknowledgeHostPlatformRiskMutation,
  useDismissHostPlatformRiskMutation,
} from "../../services/profiles.service";
import { trackHostDisintermediationViewed } from "../analytics/host-hub-analytics";

type ConsentArgs = {
  consented: boolean;
  level: HostDisintermediationRiskLevel;
};

export type UseHostDisintermediationResult = {
  /** Server-reported risk level, guaranteed to be a valid enum value. */
  level: HostDisintermediationRiskLevel;
  /** Whether the banner should currently be rendered. */
  showBanner: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Records the host's response and hides the banner on success. */
  handleConsent: (args: ConsentArgs) => Promise<void>;
};

// Lower-risk tiers are simply dismissed; higher-risk tiers require an explicit,
// server-recorded acknowledgement.
const DISMISS_LEVELS = new Set<HostDisintermediationRiskLevel>([
  HostDisintermediationRiskLevel.Educate,
  HostDisintermediationRiskLevel.SoftWarn,
]);
const ACKNOWLEDGE_LEVELS = new Set<HostDisintermediationRiskLevel>([
  HostDisintermediationRiskLevel.HardWarn,
  HostDisintermediationRiskLevel.Discipline,
]);

/**
 * Drives the Host Hub disintermediation banner. Reads the host's platform-risk
 * level from the API and decides whether to surface the banner, escalating from
 * a gentle reminder (Educate) to disciplinary action (Discipline). A lower-risk
 * banner is simply dismissed; a higher-risk one is acknowledged and then
 * dismissed, so both endpoints are called. The banner only hides once every
 * required mutation succeeds, so an acknowledgement is never lost silently.
 */
const useHostDisintermediation = (): UseHostDisintermediationResult => {
  // Tracks the host's dismissal within this session, independent of the server
  // `dismissed_recently` flag so a background refetch cannot re-show the banner.
  const [locallyDismissed, setLocallyDismissed] = useState(false);
  const [analyticsSent, setAnalyticsSent] = useState(false);

  const [acknowledgeHostPlatformRisk] =
    useAcknowledgeHostPlatformRiskMutation();
  const [dismissHostPlatformRisk] = useDismissHostPlatformRiskMutation();
  const ssoid = useSelector(selectProfileSSOid);

  const { featureGates } = useContext(StatsigContext);
  const hostDisintermediationGateValue =
    (featureGates as { host_disintermediation_banner: boolean })
      ?.host_disintermediation_banner ?? false;

  const {
    data: { level: rawLevel, dismissed_recently: dismissedRecently } = {},
    isLoading,
    isError,
    isSuccess,
  } = useGetHostPlatformRiskQuery(undefined, {
    // Don't hit the endpoint at all while the gate is off.
    skip: !hostDisintermediationGateValue,
    // Refetch on remount so the server's `dismissed_recently` is respected
    // instead of a stale cached result re-showing a dismissed banner.
    refetchOnMountOrArgChange: true,
  });

  // Never trust the raw server value: narrow once and fall back to None so the
  // level handed to the Banner is provably a valid enum member.
  const level = isHostDisintermediationRiskLevel(rawLevel)
    ? rawLevel
    : HostDisintermediationRiskLevel.None;

  const showBanner =
    isSuccess &&
    hostDisintermediationGateValue &&
    !dismissedRecently &&
    !locallyDismissed &&
    level !== HostDisintermediationRiskLevel.None;

  const handleConsent = useCallback(
    async ({ consented, level: consentedLevel }: ConsentArgs) => {
      if (!consented) {
        return;
      }
      const requiresAcknowledge = ACKNOWLEDGE_LEVELS.has(consentedLevel);
      const requiresDismissal = DISMISS_LEVELS.has(consentedLevel);
      if (!requiresDismissal && !requiresAcknowledge) {
        return;
      }
      try {
        if (requiresAcknowledge) {
          await acknowledgeHostPlatformRisk().unwrap();
        }
        await dismissHostPlatformRisk().unwrap();
        setLocallyDismissed(true);
      } catch {
        // Keep the banner visible so the host can retry;
        // the acknowledgement must not be silently dropped.
      }
    },
    [acknowledgeHostPlatformRisk, dismissHostPlatformRisk],
  );

  useEffect(() => {
    if (analyticsSent || !showBanner) {
      return;
    }
    trackHostDisintermediationViewed({
      level,
      ssoid,
      location: "banner",
    });
    setAnalyticsSent(true);
  }, [analyticsSent, showBanner, level, ssoid]);

  return {
    level,
    showBanner,
    isLoading,
    isError,
    handleConsent,
  };
};

export default useHostDisintermediation;
